import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ChevronRight, TrendingUp, TrendingDown, Activity, Layers, BarChart3, Radio, AlertTriangle, Wifi, WifiOff } from "lucide-react";
import axios from "axios";
import Layout from "../components/custom/Layout";
import OctaPanel from "../components/custom/OctaPanel";
import { API } from "../contexts/AuthContext";
import { usePortfolioWS } from "../hooks/usePortfolioWS";

function cleanSym(s) {
  if (!s) return s;
  for (const suffix of ["USDT", "PERP", ".NS", ".BSE", ".L", "-USD"]) {
    if (s.toUpperCase().endsWith(suffix)) return s.slice(0, -suffix.length);
  }
  return s;
}

// Platform identity
const PLATFORM_IDENTITY = {
  demo:     { label: "DEMO",     color: "#38BDF8", icon: Radio },
  binance:  { label: "BINANCE",  color: "#F0B90B", icon: Activity },
  zerodha:  { label: "ZERODHA",  color: "#387ED1", icon: Layers },
  alpaca:   { label: "ALPACA",   color: "#F2C94C", icon: BarChart3 },
  mt5:      { label: "MT5",      color: "#2563EB", icon: Activity },
  upstox:   { label: "UPSTOX",   color: "#8B5CF6", icon: Layers },
  angelone: { label: "ANGEL ONE", color: "#EF4444", icon: BarChart3 },
  ibkr:     { label: "IBKR",     color: "#D62B2B", icon: Activity },
  tradier:  { label: "TRADIER",  color: "#22C55E", icon: Radio },
};

const RISK_COLORS = {
  low: "#22C55E", moderate: "#38BDF8", elevated: "#F59E0B", critical: "#EF4444",
};

const STATUS_MAP = {
  active: { label: "LIVE", color: "#22C55E" },
  paused: { label: "PAUSED", color: "#F59E0B" },
  error:  { label: "ERROR", color: "#EF4444" },
  syncing: { label: "SYNCING", color: "#38BDF8" },
};

// ─── Animated Number — FIX 1 ───────────────────────────────────────
function AnimatedValue({ value, prefix = "", suffix = "", color = "#E2E8F0", fontSize = "24px", fontWeight = "700" }) {
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={value}
        initial={{ opacity: 0.5, y: 2 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0.3, y: -2 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        style={{
          fontFamily: "'JetBrains Mono', monospace", fontWeight, fontSize,
          color, fontVariantNumeric: "tabular-nums", lineHeight: 1.1, letterSpacing: "-0.01em",
          display: "inline-block",
        }}
      >
        {prefix}{value}{suffix}
      </motion.span>
    </AnimatePresence>
  );
}

// ─── Platform Panel — FIX 1-4 + Risk Pulse ─────────────────────────
function PlatformPanel({ platform, delay, onClick, riskData }) {
  const identity = PLATFORM_IDENTITY[platform.connector_type] || PLATFORM_IDENTITY.demo;
  const PlatIcon = identity.icon;
  const status = STATUS_MAP[platform.status] || STATUS_MAP.active;
  const riskColor = RISK_COLORS[platform.risk_status] || RISK_COLORS.low;
  const isPnlPositive = platform.daily_pnl >= 0;

  // Risk pulse data for this platform
  const platRisk = riskData?.platforms?.find(p => p.platform_id === platform._id);
  const isElevated = platRisk && (platRisk.risk_level === "HIGH" || platRisk.risk_level === "MODERATE");

  // FIX 1: Sync time that feels alive
  const [syncDisplay, setSyncDisplay] = useState("--:--");
  useEffect(() => {
    const update = () => {
      if (platform.last_sync) {
        const d = new Date(platform.last_sync);
        const now = new Date();
        const diffSec = Math.floor((now - d) / 1000);
        if (diffSec < 60) setSyncDisplay(`${diffSec}s ago`);
        else if (diffSec < 3600) setSyncDisplay(`${Math.floor(diffSec / 60)}m ago`);
        else setSyncDisplay(d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
      }
    };
    update();
    const iv = setInterval(update, 5000);
    return () => clearInterval(iv);
  }, [platform.last_sync]);

  return (
    <OctaPanel delay={delay} onClick={onClick} className="p-6 h-full" tintColor={identity.color}
      riskHighlight={isElevated ? (platRisk.risk_level === "HIGH" ? "#EF4444" : "#F59E0B") : null}>
      {/* Header: Platform icon + name + status — FIX 2 */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 octa-clip-sm flex items-center justify-center" style={{ background: `${identity.color}12` }}>
            <PlatIcon size={16} style={{ color: identity.color }} strokeWidth={1.5} />
          </div>
          <div>
            <div style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "13px" }}
              data-testid={`platform-name-${platform._id}`}>
              {platform.name}
            </div>
            <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", letterSpacing: "0.1em", fontWeight: "500", marginTop: "1px" }}>
              <span style={{ color: identity.color }}>{identity.label}</span>
              <span style={{ color: "#1E293B" }}> / </span>
              <span style={{ color: "#475569" }}>{platform.mode === "trade_enabled" ? "TRADE" : "READ ONLY"}</span>
            </div>
          </div>
        </div>
        {/* FIX 1: Living status indicator */}
        <div className="flex items-center gap-1.5">
          <span style={{
            display: "inline-block", width: "5px", height: "5px", borderRadius: "50%",
            background: status.color, boxShadow: `0 0 6px ${status.color}40`,
            animation: status.label === "LIVE" ? "live-dot 2.5s ease-in-out infinite" : "none",
          }} />
          <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", color: status.color, letterSpacing: "0.1em", fontWeight: "600" }}>
            {status.label}
          </span>
        </div>
      </div>

      <div style={{ height: "1px", background: "rgba(255,255,255,0.04)", margin: "0 0 16px" }} />

      {/* Equity + PnL — FIX 1: animated values */}
      <div className="mb-4">
        <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", letterSpacing: "0.14em", color: "#334155", fontWeight: "500", marginBottom: "4px" }}>TOTAL EQUITY</div>
        <AnimatedValue value={platform.equity.toLocaleString("en-US", { minimumFractionDigits: 2 })} prefix="$" />
        <div className="flex items-center gap-1 mt-2" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", fontWeight: "600", color: isPnlPositive ? "#22C55E" : "#EF4444", fontVariantNumeric: "tabular-nums" }}>
          {isPnlPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          <AnimatedValue
            value={platform.daily_pnl.toFixed(2)}
            prefix={isPnlPositive ? "+" : ""}
            suffix=" USDT"
            color={isPnlPositive ? "#22C55E" : "#EF4444"}
            fontSize="12px" fontWeight="600"
          />
        </div>
      </div>

      <div style={{ height: "1px", background: "rgba(255,255,255,0.04)", margin: "0 0 14px" }} />

      {/* Risk metrics — enhanced with pulse data */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "14px" }}>
        <div>
          <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", letterSpacing: "0.12em", color: "#334155", fontWeight: "500", marginBottom: "3px" }}>POSITIONS</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "14px", fontWeight: "600", color: "#E2E8F0", fontVariantNumeric: "tabular-nums" }}>{platform.positions_count}</div>
        </div>
        <div>
          <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", letterSpacing: "0.12em", color: "#334155", fontWeight: "500", marginBottom: "3px" }}>MARGIN</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "14px", fontWeight: "600", color: platRisk?.margin_pct > 50 ? "#F59E0B" : "#38BDF8", fontVariantNumeric: "tabular-nums" }}>
            {platRisk?.margin_pct?.toFixed(1) || platform.margin_used}%
          </div>
        </div>
        <div>
          <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", letterSpacing: "0.12em", color: "#334155", fontWeight: "500", marginBottom: "3px" }}>RISK</div>
          <div style={{
            fontFamily: "'Inter Tight', sans-serif", fontSize: "12px", fontWeight: "600",
            color: platRisk?.risk_level === "HIGH" ? "#EF4444" : platRisk?.risk_level === "MODERATE" ? "#F59E0B" : "#22C55E",
            textTransform: "uppercase",
          }}>
            {platRisk?.risk_level || platform.risk_status}
          </div>
        </div>
      </div>

      {/* Exposure row from risk pulse */}
      {platRisk && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "14px" }}>
          <div>
            <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", letterSpacing: "0.12em", color: "#334155", fontWeight: "500", marginBottom: "3px" }}>EXPOSURE</div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", fontWeight: "600", color: platRisk.exposure_pct > 200 ? "#EF4444" : "#94A3B8", fontVariantNumeric: "tabular-nums" }}>
              {platRisk.exposure_pct?.toFixed(1)}%
            </div>
          </div>
          {platRisk.liquidation_buffer_pct !== null && (
            <div>
              <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", letterSpacing: "0.12em", color: "#334155", fontWeight: "500", marginBottom: "3px" }}>LIQ BUFFER</div>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", fontWeight: "600",
                color: platRisk.liquidation_buffer_pct < 5 ? "#EF4444" : platRisk.liquidation_buffer_pct < 15 ? "#F59E0B" : "#22C55E",
                fontVariantNumeric: "tabular-nums",
              }}>
                {platRisk.liquidation_buffer_pct?.toFixed(1)}%
              </div>
            </div>
          )}
        </div>
      )}

      {/* Top gainer / loser */}
      {(platform.top_gainer || platform.top_loser) && (
        <>
          <div style={{ height: "1px", background: "rgba(255,255,255,0.04)", margin: "0 0 12px" }} />
          <div className="flex gap-4">
            {platform.top_gainer && (
              <div className="flex items-center gap-1.5">
                <TrendingUp size={10} style={{ color: "#22C55E" }} />
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "#22C55E", fontVariantNumeric: "tabular-nums" }}>{cleanSym(platform.top_gainer.symbol)}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "rgba(34,197,94,0.5)", fontVariantNumeric: "tabular-nums" }}>+{platform.top_gainer.pnl.toFixed(0)}</span>
              </div>
            )}
            {platform.top_loser && (
              <div className="flex items-center gap-1.5">
                <TrendingDown size={10} style={{ color: "#EF4444" }} />
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "#EF4444", fontVariantNumeric: "tabular-nums" }}>{cleanSym(platform.top_loser.symbol)}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "rgba(239,68,68,0.5)", fontVariantNumeric: "tabular-nums" }}>{platform.top_loser.pnl.toFixed(0)}</span>
              </div>
            )}
          </div>
        </>
      )}

      {/* Risk alert for this platform */}
      {isElevated && (
        <>
          <div style={{ height: "1px", background: "rgba(255,255,255,0.04)", margin: "12px 0" }} />
          <div className="flex items-center gap-2" data-testid={`platform-risk-alert-${platform._id}`}>
            <AlertTriangle size={10} style={{ color: platRisk.risk_level === "HIGH" ? "#EF4444" : "#F59E0B" }} />
            <span style={{
              fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "10px",
              color: platRisk.risk_level === "HIGH" ? "#EF4444" : "#F59E0B",
            }}>
              {platRisk.risk_level === "HIGH" ? "High risk — reduce exposure" : "Elevated risk — monitor closely"}
            </span>
          </div>
        </>
      )}

      {/* Footer — FIX 1: live sync display */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "14px", paddingTop: "10px", borderTop: "1px solid rgba(255,255,255,0.03)" }}>
        <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", color: "#334155", letterSpacing: "0.1em", fontWeight: "500" }}>
          SYNCED {syncDisplay}
        </span>
        <div className="flex items-center gap-1 transition-colors duration-200 group-hover:text-[#94A3B8]" style={{ color: "#334155" }}>
          <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", letterSpacing: "0.08em", fontWeight: "500" }}>WORKSPACE</span>
          <ChevronRight size={10} className="transition-transform duration-200 group-hover:translate-x-0.5" />
        </div>
      </div>
    </OctaPanel>
  );
}

// ─── Empty Slot Panel — FIX 3 ──────────────────────────────────────
function EmptySlotPanel({ delay, onClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <div
        className="group cursor-pointer h-full"
        onClick={onClick}
        style={{
          clipPath: "polygon(16px 0%, calc(100% - 16px) 0%, 100% 16px, 100% calc(100% - 16px), calc(100% - 16px) 100%, 16px 100%, 0% calc(100% - 16px), 0% 16px)",
          background: "rgba(255,255,255,0.015)",
          border: "1px dashed rgba(37,99,235,0.12)",
          transition: "all 0.3s ease",
        }}
      >
        <div className="flex flex-col items-center justify-center h-full min-h-[300px] transition-all duration-300 group-hover:bg-white/[0.015]">
          {/* Icon */}
          <div className="w-14 h-14 octa-clip-sm flex items-center justify-center mb-5 transition-all duration-300 group-hover:scale-105"
            style={{ background: "rgba(37,99,235,0.04)", border: "1px dashed rgba(37,99,235,0.15)" }}>
            <Plus size={22} style={{ color: "#2563EB" }} className="transition-transform duration-300 group-hover:rotate-90" />
          </div>
          <div style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#475569", fontSize: "12px", marginBottom: "4px", transition: "color 0.3s" }}
            className="group-hover:text-[#94A3B8]">
            Connect Platform
          </div>
          <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "10px", color: "#334155" }}>
            Add trading venue
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Command Wall Page ─────────────────────────────────────────────
export default function CommandWall() {
  const navigate = useNavigate();
  const [platforms, setPlatforms] = useState([]);
  const [summary, setSummary] = useState(null);
  const [riskPulse, setRiskPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wsToken, setWsToken] = useState(null);

  // Fetch WS token on mount
  useEffect(() => {
    axios.get(`${API}/auth/ws-token`).then(r => setWsToken(r.data.token)).catch(() => {});
  }, []);

  const { connected: wsConnected, lastMessage } = usePortfolioWS(wsToken);

  const fetchData = useCallback(async () => {
    try {
      const [wallRes, sumRes, riskRes] = await Promise.all([
        axios.get(`${API}/platforms/wall`),
        axios.get(`${API}/dashboard/summary`),
        axios.get(`${API}/risk/pulse`).catch(() => ({ data: null })),
      ]);
      setPlatforms(wallRes.data);
      setSummary(sumRes.data);
      if (riskRes.data) setRiskPulse(riskRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 15000);
    return () => clearInterval(iv);
  }, [fetchData]);

  // Refresh on WS sync_update
  useEffect(() => {
    if (lastMessage?.type === "sync_update") fetchData();
  }, [lastMessage, fetchData]);

  const TOTAL_SLOTS = 6;
  const emptySlots = platforms.length < TOTAL_SLOTS ? TOTAL_SLOTS - platforms.length : 0;

  return (
    <Layout summary={summary}>
      <div className="p-7 h-full overflow-auto" data-testid="command-wall">
        {/* Header with Add Platform button */}
        <div className="mb-7">
          <div className="flex items-start justify-between">
            <div>
              <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", letterSpacing: "0.2em", color: "#334155", fontWeight: "500", marginBottom: "4px" }}>
                SYSTEM VIEW
              </div>
              <h1 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0", fontSize: "15px", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Command Wall
              </h1>
              <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", color: "#334155", letterSpacing: "0.1em", marginTop: "3px", fontWeight: "500" }}>
                {platforms.length} CONNECTED PLATFORM{platforms.length !== 1 ? "S" : ""}
              </div>
              {/* Live connection indicator */}
              <div className="flex items-center gap-1.5 mt-2" data-testid="ws-status">
                {wsConnected ? (
                  <>
                    <div className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8px", color: "#22C55E", letterSpacing: "0.08em" }}>LIVE</span>
                  </>
                ) : (
                  <>
                    <div className="w-1.5 h-1.5 rounded-full bg-[#475569]" />
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8px", color: "#475569", letterSpacing: "0.08em" }}>POLLING</span>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={() => navigate("/onboarding")}
              data-testid="add-platform-btn"
              className="flex items-center gap-2 px-4 py-2 transition-all duration-200 hover:bg-white/[0.04]"
              style={{
                clipPath: "polygon(6px 0%, calc(100% - 6px) 0%, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0% calc(100% - 6px), 0% 6px)",
                border: "1px solid rgba(37,99,235,0.2)",
                background: "rgba(37,99,235,0.04)",
              }}
            >
              <Plus size={13} style={{ color: "#2563EB" }} />
              <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", fontWeight: "600", letterSpacing: "0.1em", color: "#2563EB" }}>
                ADD PLATFORM
              </span>
            </button>
          </div>
          <div style={{ height: "1px", background: "rgba(255,255,255,0.04)", marginTop: "14px" }} />
        </div>

        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="octa-clip animate-pulse" style={{ height: "300px", background: "#111318" }} />
            ))}
          </div>
        )}

        {/* Platform grid — 2x3 Command Wall — FIX 7: increased gap */}
        {!loading && (
          <>
            {/* Global risk alert banner */}
            {riskPulse?.alerts?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-5 px-4 py-3 flex items-center gap-3"
                data-testid="global-risk-alert"
                style={{
                  clipPath: "polygon(8px 0%, calc(100% - 8px) 0%, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0% calc(100% - 8px), 0% 8px)",
                  background: riskPulse.overall_risk === "HIGH"
                    ? "rgba(239,68,68,0.04)"
                    : riskPulse.overall_risk === "MODERATE"
                    ? "rgba(245,158,11,0.04)"
                    : "rgba(34,197,94,0.04)",
                  border: `1px solid ${
                    riskPulse.overall_risk === "HIGH"
                      ? "rgba(239,68,68,0.12)"
                      : riskPulse.overall_risk === "MODERATE"
                      ? "rgba(245,158,11,0.12)"
                      : "rgba(34,197,94,0.12)"
                  }`,
                }}
              >
                <AlertTriangle size={13} style={{
                  color: riskPulse.overall_risk === "HIGH" ? "#EF4444" : riskPulse.overall_risk === "MODERATE" ? "#F59E0B" : "#22C55E",
                  flexShrink: 0,
                }} />
                <span style={{
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "11px",
                  color: riskPulse.overall_risk === "HIGH" ? "#EF4444" : riskPulse.overall_risk === "MODERATE" ? "#F59E0B" : "#22C55E",
                }}>
                  {riskPulse.alerts[0].message}
                </span>
                {riskPulse.alerts.length > 1 && (
                  <span style={{
                    fontFamily: "'Inter Tight', sans-serif", fontSize: "9px",
                    color: "#475569", marginLeft: "auto",
                  }}>
                    +{riskPulse.alerts.length - 1} more
                  </span>
                )}
              </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="platform-grid">
              {platforms.map((plat, i) => (
                <PlatformPanel
                  key={plat._id}
                  platform={plat}
                  delay={i * 0.07}
                  onClick={() => navigate(`/platform/${plat._id}`)}
                  riskData={riskPulse}
                />
              ))}
              {/* Always show at least 1 empty slot, fill up to 6 min */}
              {Array.from({ length: Math.max(1, emptySlots) }).map((_, i) => (
                <EmptySlotPanel
                  key={`empty-${i}`}
                  delay={(platforms.length + i) * 0.07}
                  onClick={() => navigate("/onboarding")}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
